import React, { useState } from 'react';
import { ShoppingCart, Info } from 'lucide-react';
import { Card, CardContent, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';

export const ProductCard = ({ product, onAddToCart }) => {
  const [selectedVariant, setSelectedVariant] = useState(product.variants[0]);
  const [showDialog, setShowDialog] = useState(false);

  const handleAddToCart = () => {
    onAddToCart({
      ...product,
      selectedVariant,
      cartItemId: `${product.id}-${selectedVariant.size}`
    });
  };

  return (
    <Card className="group overflow-hidden border-honey-100 hover:border-honey-300 hover:shadow-xl transition-all duration-300">
      {/* Product Image */}
      <div className="relative overflow-hidden bg-honey-50 aspect-square">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        {product.featured && (
          <Badge className="absolute top-3 right-3 bg-honey-600 text-white border-none">
            Popular
          </Badge>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </div>

      <CardContent className="p-4">
        {/* Category */}
        <p className="text-xs font-medium text-honey-600 uppercase tracking-wide mb-2">
          {product.category}
        </p>

        {/* Product Name */}
        <h3 className="text-lg font-bold text-honey-950 mb-2 line-clamp-2">
          {product.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-honey-900/70 mb-4 line-clamp-2">
          {product.description}
        </p>

        {/* Size Selection */}
        {product.variants.length > 1 ? (
          <div className="mb-4">
            <Label className="text-xs font-medium text-honey-900 mb-2 block">Tamaño:</Label>
            <RadioGroup
              value={selectedVariant.size}
              onValueChange={(value) => {
                const variant = product.variants.find(v => v.size === value);
                setSelectedVariant(variant);
              }}
              className="flex flex-wrap gap-2"
            >
              {product.variants.map((variant) => (
                <div key={variant.size} className="flex items-center">
                  <RadioGroupItem
                    value={variant.size}
                    id={`${product.id}-${variant.size}`}
                    className="sr-only peer"
                  />
                  <Label
                    htmlFor={`${product.id}-${variant.size}`}
                    className="flex items-center justify-center px-3 py-1.5 text-xs font-medium border-2 border-honey-200 rounded-md cursor-pointer hover:border-honey-400 peer-data-[state=checked]:border-honey-600 peer-data-[state=checked]:bg-honey-50 transition-colors"
                  >
                    {variant.size}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        ) : (
          <p className="text-xs text-honey-700 mb-4">Tamaño: {product.variants[0].size}</p>
        )}

        {/* Price */}
        <div className="flex items-baseline space-x-2 mb-3">
          <span className="text-2xl font-bold text-honey-900">
            ${selectedVariant.price}
          </span>
          <span className="text-sm text-honey-700">MXN</span>
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0 flex gap-2">
        <Button
          className="flex-1 bg-honey-600 hover:bg-honey-700 text-white font-semibold transition-colors"
          onClick={handleAddToCart}
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Agregar
        </Button>
        
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="border-honey-300 hover:bg-honey-50 hover:border-honey-400"
            >
              <Info className="w-4 h-4 text-honey-900" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl text-honey-950">{product.name}</DialogTitle>
              <DialogDescription className="text-honey-700">
                {product.category}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-64 object-cover rounded-lg"
              />
              <div>
                <h4 className="font-semibold text-honey-950 mb-2">Descripción</h4>
                <p className="text-honey-900/80">{product.description}</p>
              </div>
              {product.ingredients && (
                <div>
                  <h4 className="font-semibold text-honey-950 mb-2">Ingredientes clave</h4>
                  <div className="flex flex-wrap gap-2">
                    {product.ingredients.map((ingredient, index) => (
                      <Badge key={index} variant="outline" className="border-honey-300 text-honey-900">
                        {ingredient}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <h4 className="font-semibold text-honey-950 mb-2">Opciones disponibles</h4>
                {product.variants.map((variant, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-honey-100 last:border-0">
                    <span className="text-honey-900">{variant.size}</span>
                    <span className="font-semibold text-honey-900">${variant.price} MXN</span>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardFooter>
    </Card>
  );
};
